import { useApiClient } from '@m43/core';
import {
    ApiResultBoloAdditionalInfoPinResponse,
    ApiResultBoloAdditionalInfoPriorityResponse,
    ApiResultDeleteBoloAdditionalInfoResponse,
    ApiResultEditBoloAdditionalInfoResponse,
    ApiResultGetBoloAdditionalInfosResponse,
    ApiResultGetPinnedBoloAdditionalInfosResponse,
    ApiResultSaveBoloAdditionalInfoResponse,
    SaveBoloAdditionalInfoRequest,
    BoloResource,
} from '@mark43/cad-api';
import { AxiosResponse } from 'axios-cad-nx';
import { useCallback, useMemo } from 'react';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useAdditionalInfosBoloResource() {
    const apiClient = useApiClient();

    /**
     * Deletes an Additional Info.
     *
     * @param boloInfoId - ID of the Additional Infos to delete.
     * @returns No value returned.
     * @throws The `AXIOSResponse` of a failed request to delete the Additional Info.
     */
    const deleteInfo = useCallback(
        async (
            boloInfoId: number
        ): Promise<AxiosResponse<ApiResultDeleteBoloAdditionalInfoResponse>> => {
            return await apiClient.request<BoloResource.DeleteBoloAdditionalInfos>({
                method: 'POST',
                url: `bolo/additional_info/delete`,
                data: {
                    boloLinkId: boloInfoId,
                },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    /**
     * Retrieves a list of Additional Infos for a specified BOLO.
     *
     * @param boloId - ID of the BOLO to retrieve the Additional Infos for.
     * @param cursor - If cursor is present, the list will start one ID past the cursor (direction is dependent on `direction`).
     *                 If cursor is not present the list will start at the newest or oldest Additional Info, depending on `direction`).
     * @param limit - The maximum number of Additional Infos to return.
     * @returns The `AXIOSResponse` of a successful request to get the Additional Infos list.
     * @throws The `AXIOSResponse` of a failed request to get the Additional Infos list.
     */
    const getInfosByCursor = useCallback(
        async ({
            boloId,
            limit: pageSize = 100,
            cursor,
        }: {
            limit?: number;
            cursor?: number;
            boloId: number;
        }): Promise<AxiosResponse<ApiResultGetBoloAdditionalInfosResponse>> => {
            return await apiClient.request<BoloResource.GetBoloAdditionalInfos>({
                method: 'POST',
                url: `bolo/additional_info`,
                data: { boloId, lastId: cursor, size: pageSize },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    /**
     * Create a new Additional Info/Narrative
     *
     * @param data - The new additional info to be created.
     * @returns The `AXIOSResponse` of a successful request to create the additional info.
     * @throws The `AXIOSResponse` of a failed request to create the additional info.
     */
    const createAdditionalInfo = useCallback(
        async (
            data: SaveBoloAdditionalInfoRequest
        ): Promise<AxiosResponse<ApiResultSaveBoloAdditionalInfoResponse>> => {
            return await apiClient.request<BoloResource.SaveBoloAdditionalInfo>({
                method: 'PUT',
                url: `bolo/additional_info`,
                data,
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    /**
     * Edits a BOLO Additional Info text.
     *
     * @param boloLinkId - ID of the BOLO Additional Info link to edit (maps to store `id`).
     * @param text - Updated text content.
     * @returns The `AxiosResponse` of a successful request to edit the Additional Info.
     * @throws The `AxiosResponse` of a failed request to edit the Additional Info.
     */
    const editInfo = useCallback(
        async ({
            boloLinkId,
            text,
        }: {
            boloLinkId: number;
            text?: string | null;
        }): Promise<AxiosResponse<ApiResultEditBoloAdditionalInfoResponse>> => {
            return await apiClient.request<BoloResource.EditBoloAdditionalInfo>({
                method: 'POST',
                url: `bolo/additional_info/edit`,
                data: { boloLinkId, text } as BoloResource.EditBoloAdditionalInfo['data'],
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    /**
     * Sets the priority of a BOLO Additional Info (emphasize / de-emphasize).
     *
     * @param boloLinkId - ID of the BOLO Additional Info link (maps to store `id`).
     * @param priority - The new priority value.
     * @returns The `AxiosResponse` carrying the re-mapped view with the new priority.
     * @throws The `AxiosResponse` of a failed request.
     */
    const setPriority = useCallback(
        async ({
            boloLinkId,
            priority,
        }: {
            boloLinkId: number;
            priority: string;
        }): Promise<AxiosResponse<ApiResultBoloAdditionalInfoPriorityResponse>> => {
            return await apiClient.request<BoloResource.SetBoloAdditionalInfoPriority>({
                method: 'POST',
                url: `bolo/additional_info/priority`,
                data: {
                    boloLinkId,
                    priority,
                } as BoloResource.SetBoloAdditionalInfoPriority['data'],
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    /**
     * Retrieves the pinned Additional Infos for a BOLO (newest pin first).
     *
     * @param boloId - ID of the BOLO to retrieve pinned Additional Infos for.
     * @returns The `AxiosResponse` of a successful request to get the pinned list.
     * @throws The `AxiosResponse` of a failed request to get the pinned list.
     */
    const getPinnedNarratives = useCallback(
        async ({
            boloId,
        }: {
            boloId: number;
        }): Promise<AxiosResponse<ApiResultGetPinnedBoloAdditionalInfosResponse>> => {
            return await apiClient.request<BoloResource.GetPinnedBoloAdditionalInfos>({
                method: 'POST',
                url: `bolo/additional_info/pinned`,
                data: { boloId },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    /**
     * Pins a BOLO Additional Info.
     *
     * @param boloLinkId - ID of the BOLO Additional Info link to pin (maps to store `id`).
     * @returns The `AxiosResponse` carrying the re-mapped view with pin state.
     * @throws The `AxiosResponse` of a failed request (e.g. the pin-cap HTTP 400).
     */
    const pinNarrative = useCallback(
        async ({
            boloLinkId,
        }: {
            boloLinkId: number;
        }): Promise<AxiosResponse<ApiResultBoloAdditionalInfoPinResponse>> => {
            return await apiClient.request<BoloResource.PinBoloAdditionalInfo>({
                method: 'POST',
                url: `bolo/additional_info/pin`,
                data: { action: 'PIN', boloLinkId },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    const unpinNarrative = useCallback(
        async ({
            boloLinkId,
        }: {
            boloLinkId: number;
        }): Promise<AxiosResponse<ApiResultBoloAdditionalInfoPinResponse>> => {
            return await apiClient.request<BoloResource.PinBoloAdditionalInfo>({
                method: 'POST',
                url: `bolo/additional_info/pin`,
                data: { action: 'UNPIN', boloLinkId },
                shouldFailSilently: true,
            });
        },
        [apiClient]
    );

    // Stable identity matters: downstream useMemo (controller in use-additional-infos)
    // depends on this object, and a new ref every render makes its effects re-run on every render.
    return useMemo(
        () => ({
            createAdditionalInfo,
            deleteInfo,
            editInfo,
            setPriority,
            getInfosByCursor,
            getPinnedNarratives,
            pinNarrative,
            unpinNarrative,
        }),
        [
            createAdditionalInfo,
            deleteInfo,
            editInfo,
            setPriority,
            getInfosByCursor,
            getPinnedNarratives,
            pinNarrative,
            unpinNarrative,
        ]
    );
}
